import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ApiContext } from '../../../../../context/MyContext';
import { useUtils } from '../../../../../context/UtilsContext';
import { useWorkoutData } from '../../../core/providers/WorkoutDataProvider';
import { modifyTargetPlanDurations, modifyTargetPlans } from '../../../core/api/workoutApi';
import { buildWeekdayDetails } from '../../../core/utils/buildWeekdayDetails';
import { assignTemplateToWeekday } from '../../../core/utils/assignTemplateToWeekday';
import { replaceTemplateOnWeekday } from '../../../core/utils/replaceTemplateOnWeekday';
import { assignPlanToWeekdays } from '../../../core/utils/assignPlanToWeekdays';
import { clearWeekdayToRest } from '../../../core/utils/clearWeekdayToRest';
import {
  displayDate,
  durationOverlapsRange,
  durationPlanCategory,
  fetchDurationById,
  fetchDurationsInWindow,
} from '../../../core/utils/assignedPlansUtils';
import {
  formatScheduleDate,
  pickFirstPlanSelection,
  planStillInWeekdayDetails,
  resolveWeekdayPlan,
} from '../../../core/utils/scheduleEditorUtils';
import { durationPlanName } from '../../../core/utils/durationPlanName';
import { useWorkoutPlanEventTypeId } from '../../plan/eventTypes';
import WorkoutScheduleSummary from '../../../ui/schedule/WorkoutScheduleSummary';
import WorkoutEditSection from '../../plan/modify/WorkoutEditSection';
import WorkoutTemplatePickerModal from '../../plan/modify/WorkoutTemplatePickerModal';
import ChangeTemplateConfirmationModal from '../../plan/modify/ChangeTemplateConfirmationModal';
import MarkAsRestConfirmationModal from '../../plan/modify/MarkAsRestConfirmationModal';
import UseWorkoutOnOtherDaysModal from '../../plan/modify/UseWorkoutOnOtherDaysModal';
import { Toaster } from '../../../../../common/Toaster/Toaster';

export default function WorkoutDurationDetailView({ duration, userId, onDurationChange }) {
  const { get, post } = useContext(ApiContext);
  const { workoutCommonUtils } = useUtils();
  const { workoutCommonUtils: workoutDataUtils } = useWorkoutData();
  const eventTypeId = useWorkoutPlanEventTypeId();

  const weekdays = useMemo(
    () => workoutCommonUtils?.weekdays || workoutDataUtils?.weekdays || [],
    [workoutCommonUtils, workoutDataUtils]
  );

  const [startDate, setStartDate] = useState(() => displayDate(duration?.start_date));
  const [endDate, setEndDate] = useState(() => displayDate(duration?.end_date));
  const [savingDates, setSavingDates] = useState(false);
  const [savingPlanDetails, setSavingPlanDetails] = useState(false);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('default');
  const [assigningWeekday, setAssigningWeekday] = useState(null);
  const [isAssigningWeekday, setIsAssigningWeekday] = useState(false);
  const [changeTemplateConfirmOpen, setChangeTemplateConfirmOpen] = useState(false);
  const [markAsRestConfirmOpen, setMarkAsRestConfirmOpen] = useState(false);
  const [isMarkingAsRest, setIsMarkingAsRest] = useState(false);
  const [useOnOtherDaysOpen, setUseOnOtherDaysOpen] = useState(false);
  const [workoutDurationSummary, setWorkoutDurationSummary] = useState(null);

  useEffect(() => {
    setWorkoutDurationSummary(null);
  }, [selectedPlan?.target_plan_id]);

  const weekdayDetails = useMemo(() => {
    if (!duration) return {};
    return buildWeekdayDetails(
      duration.weekdays,
      duration.start_date,
      duration.end_date,
      weekdays
    );
  }, [duration, weekdays]);

  const planName = useMemo(() => {
    if (selectedPlan?.title) return String(selectedPlan.title).trim();
    return durationPlanName(duration);
  }, [duration, selectedPlan]);

  const planDescription = useMemo(() => {
    if (selectedPlan?.description) return String(selectedPlan.description).trim();
    const seen = new Set();
    const descriptions = [];
    for (const day of duration?.weekdays || []) {
      for (const plan of day.plans || []) {
        const d = (plan.description || '').trim();
        if (!d) continue;
        const key = d.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        descriptions.push(d);
      }
    }
    return descriptions.length === 1 ? descriptions[0] : '';
  }, [duration, selectedPlan]);

  useEffect(() => {
    setStartDate(displayDate(duration?.start_date));
    setEndDate(displayDate(duration?.end_date));
  }, [duration?.duration_id, duration?.start_date, duration?.end_date]);

  useEffect(() => {
    if (!weekdayDetails || Object.keys(weekdayDetails).length === 0) return;
    if (
      selectedPlan?.target_plan_id &&
      planStillInWeekdayDetails(weekdayDetails, selectedPlan.target_plan_id)
    ) {
      return;
    }
    const first = pickFirstPlanSelection(weekdayDetails);
    if (first) setSelectedPlan(first);
  }, [weekdayDetails]); // eslint-disable-line

  useEffect(() => {
    if (!alertMessage) return;
    const t = setTimeout(() => setAlertMessage(''), 3000);
    return () => clearTimeout(t);
  }, [alertMessage]);

  const refreshDuration = useCallback(async () => {
    if (!eventTypeId || !userId || !duration?.duration_id) return null;
    try {
      const updated = await fetchDurationById(get, {
        userId,
        eventTypeId,
        durationId: duration.duration_id,
      });
      if (updated) {
        onDurationChange?.(updated);
        return updated;
      }
    } catch (err) {
      console.error('Failed to refresh duration:', err);
    }
    return null;
  }, [eventTypeId, userId, duration?.duration_id, get, onDurationChange]);

  const handleSaveDates = async (start = startDate, end = endDate) => {
    if (!start || !end) {
      setAlertMessage('Please select valid dates.');
      setAlertType('warning');
      return;
    }
    if (start >= end) {
      setAlertMessage('End date must be after start date.');
      setAlertType('error');
      return;
    }
    setStartDate(start);
    setEndDate(end);
    setSavingDates(true);
    try {
      const durations = await fetchDurationsInWindow(get, { userId, eventTypeId, windowDays: 90 });
      const hasOverlap = durations.some(
        (d) =>
          String(d.duration_id) !== String(duration.duration_id) &&
          durationPlanCategory(d, userId) !== 'personal' &&
          durationOverlapsRange(d, start, end)
      );
      if (hasOverlap) {
        setAlertMessage('A schedule already exists for this date range. Please choose different dates.');
        setAlertType('error');
        return;
      }

      const result = await modifyTargetPlanDurations(post, {
        event_type_id: eventTypeId,
        target_plan_durations: [
          {
            operation: 'UPDATE',
            id: duration.duration_id,
            assigned_to_id: userId,
            active_from: start,
            active_to: end,
          },
        ],
      });
      if (result?.status === 200) {
        setAlertMessage('Plan dates updated.');
        setAlertType('success');
        await refreshDuration();
      } else {
        setAlertMessage('Failed to update plan dates.');
        setAlertType('error');
      }
    } catch (e) {
      console.error('Error updating duration:', e);
      setAlertMessage('Failed to update plan dates.');
      setAlertType('error');
    } finally {
      setSavingDates(false);
    }
  };

  const handleSavePlanDetails = async (title, description) => {
    if (!title) {
      setAlertMessage('Plan name is required.');
      setAlertType('warning');
      return false;
    }
    if (!eventTypeId) {
      setAlertMessage('Event type not found. Please refresh.');
      setAlertType('error');
      return false;
    }

    const plansById = new Map();
    if (selectedPlan?.target_plan_id) {
      plansById.set(String(selectedPlan.target_plan_id), selectedPlan);
    } else {
      for (const day of duration?.weekdays || []) {
        for (const plan of day.plans || []) {
          const id = plan.target_plan_id || plan.plan_id || plan.id;
          if (id) plansById.set(String(id), plan);
        }
      }
    }

    if (plansById.size === 0) {
      setAlertMessage('Select a day with a workout plan to rename.');
      setAlertType('warning');
      return false;
    }

    setSavingPlanDetails(true);
    try {
      const desc =
        description ||
        [...plansById.values()][0]?.description ||
        title;
      const result = await modifyTargetPlans(post, {
        event_type_id: eventTypeId,
        target_plans: [...plansById.entries()].map(([id, plan]) => ({
          operation: 'UPDATE',
          id,
          title,
          description: description || plan.description || desc,
          assigned_to_id: userId,
        })),
      });
      if (!result?.success) {
        setAlertMessage(result?.message || 'Failed to update plan name.');
        setAlertType('error');
        return false;
      }
      setAlertMessage(
        plansById.size > 1 ? 'Plan names updated.' : 'Plan name updated.'
      );
      setAlertType('success');
      const updated = await refreshDuration();
      if (updated && selectedPlan?.target_plan_id) {
        const refreshed = resolveWeekdayPlan(
          buildWeekdayDetails(
            updated.weekdays,
            updated.start_date,
            updated.end_date,
            weekdays
          ),
          selectedPlan.weekday_id,
          { ...selectedPlan, title, description: description || selectedPlan.description }
        );
        if (refreshed) setSelectedPlan(refreshed);
      }
      return true;
    } catch (e) {
      console.error('Error updating plan details:', e);
      setAlertMessage('Failed to update plan name.');
      setAlertType('error');
      return false;
    } finally {
      setSavingPlanDetails(false);
    }
  };

  const handleSelectPlan = (plan, weekdayId, weekdayName) => {
    setSelectedPlan({
      ...plan,
      weekday_id: weekdayId,
      weekday_name: weekdayName,
    });
  };

  const handleJumpToDay = (weekdayId, day) => {
    const plan = day.plans?.[0];
    if (plan) {
      handleSelectPlan(plan, weekdayId, day.name);
    } else {
      setAssigningWeekday({ id: weekdayId, name: day.name });
    }
  };

  const handleTemplateSelect = async ({ templateId, templateName }) => {
    if (!assigningWeekday || !duration?.duration_id) return;
    setIsAssigningWeekday(true);
    try {
      const isReplace = Boolean(assigningWeekday.replacePlan);
      const assignFn = isReplace ? replaceTemplateOnWeekday : assignTemplateToWeekday;
      const result = await assignFn({
        post,
        eventTypeId,
        assigneeId: userId,
        durationId: duration.duration_id,
        weekdayId: assigningWeekday.id,
        weekdayName: assigningWeekday.name,
        templateId,
        templateName,
        weekdayDetails,
        ...(isReplace ? { planToReplace: assigningWeekday.replacePlan } : {}),
      });
      if (!result.success) {
        setAlertMessage(result.message);
        setAlertType('error');
        throw new Error(result.message);
      }
      setAlertMessage(
        isReplace ? 'Template changed — edit exercises below' : 'Workout assigned — edit exercises below'
      );
      setAlertType('success');
      const updated = await refreshDuration();
      if (updated) {
        const rebuilt = buildWeekdayDetails(
          updated.weekdays,
          updated.start_date,
          updated.end_date,
          weekdays
        );
        const day =
          rebuilt[assigningWeekday.id] ||
          Object.values(rebuilt).find(
            (d) => String(d.weekday_id) === String(assigningWeekday.id)
          );
        const newPlan =
          day?.plans?.find((p) => p.target_plan_id === result.planId) || day?.plans?.[0];
        if (newPlan) {
          setSelectedPlan({
            ...newPlan,
            weekday_id: assigningWeekday.id,
            weekday_name: assigningWeekday.name,
          });
        }
      }
    } catch (e) {
      if (!e?.message) {
        console.error('Assign error:', e);
        setAlertMessage('Failed to assign workout.');
        setAlertType('error');
      }
      throw e;
    } finally {
      setIsAssigningWeekday(false);
      setAssigningWeekday(null);
    }
  };

  const handleChangeTemplateRequest = () => {
    if (!selectedPlan?.target_plan_id) return;
    setChangeTemplateConfirmOpen(true);
  };

  const handleConfirmChangeTemplate = () => {
    if (!selectedPlan?.weekday_id) return;
    setChangeTemplateConfirmOpen(false);
    const planToReplace = resolveWeekdayPlan(
      weekdayDetails,
      selectedPlan.weekday_id,
      selectedPlan
    );
    setAssigningWeekday({
      id: selectedPlan.weekday_id,
      name: selectedPlan.weekday_name,
      replacePlan: planToReplace,
    });
  };

  const assignableWeekdays = useMemo(() => {
    if (!selectedPlan?.target_plan_id || !selectedPlan?.weekday_id) return [];
    return Object.entries(weekdayDetails)
      .map(([key, day]) => {
        const weekdayId = day.weekday_id ?? key;
        const existing = day.plans?.[0];
        const hasSamePlan =
          existing?.target_plan_id != null &&
          String(existing.target_plan_id) === String(selectedPlan.target_plan_id);
        return {
          weekdayId,
          name: day.name,
          hasSamePlan,
          hasOtherPlan: Boolean(existing?.target_plan_id) && !hasSamePlan,
          existingTitle: existing?.title || null,
          targetPlanId: existing?.target_plan_id || null,
          isSource: String(weekdayId) === String(selectedPlan.weekday_id),
        };
      })
      .filter((d) => !d.isSource);
  }, [weekdayDetails, selectedPlan?.target_plan_id, selectedPlan?.weekday_id]);

  const handleUseOnOtherDays = async (targetWeekdayIds) => {
    if (!selectedPlan?.target_plan_id || !duration?.duration_id) return;
    const result = await assignPlanToWeekdays({
      post,
      eventTypeId,
      durationId: duration.duration_id,
      sourcePlan: selectedPlan,
      targetWeekdayIds,
      weekdayDetails,
    });
    if (!result.success) {
      setAlertMessage(result.message || 'Failed to assign plan to other days.');
      setAlertType('error');
      throw new Error(result.message);
    }
    setAlertMessage(result.message || 'Plan assigned to other days.');
    setAlertType(result.warning ? 'warning' : 'success');
    await refreshDuration();
  };

  const handleMarkAsRestRequest = () => {
    if (!selectedPlan?.target_plan_id) return;
    setMarkAsRestConfirmOpen(true);
  };

  const handleConfirmMarkAsRest = async () => {
    if (!selectedPlan?.weekday_id) return;
    const planToClear = resolveWeekdayPlan(
      weekdayDetails,
      selectedPlan.weekday_id,
      selectedPlan
    );
    setIsMarkingAsRest(true);
    try {
      const result = await clearWeekdayToRest({
        post,
        eventTypeId,
        planToClear,
        weekdayName: selectedPlan.weekday_name,
        weekdayDetails,
      });
      if (!result.success) {
        setAlertMessage(result.message || 'Failed to mark day as rest.');
        setAlertType('error');
        return;
      }
      setMarkAsRestConfirmOpen(false);
      setAlertMessage(result.message || 'Day is now a rest day.');
      setAlertType(result.warning ? 'warning' : 'success');
      setSelectedPlan(null);
      await refreshDuration();
    } catch (e) {
      console.error('Mark as rest error:', e);
      setAlertMessage('Failed to mark day as rest.');
      setAlertType('error');
    } finally {
      setIsMarkingAsRest(false);
    }
  };

  if (!duration) {
    return <div className="text-center py-12 text-gray-600">Plan not found.</div>;
  }

  return (
    <div className="w-full">
      {alertMessage && <Toaster message={alertMessage} type={alertType} />}

      <WorkoutScheduleSummary
        planName={planName}
        planDescription={planDescription}
        weekdayDetails={weekdayDetails}
        startDate={startDate}
        endDate={endDate}
        startDateLabel={formatScheduleDate(duration.start_date)}
        endDateLabel={formatScheduleDate(duration.end_date)}
        onSaveDates={handleSaveDates}
        savingDates={savingDates}
        onSavePlanDetails={handleSavePlanDetails}
        savingPlanDetails={savingPlanDetails}
        selectedWeekdayId={selectedPlan?.weekday_id}
        assigningWeekdayId={isAssigningWeekday ? assigningWeekday?.id : null}
        onJumpToDay={handleJumpToDay}
        durationSummary={workoutDurationSummary}
      />

      {selectedPlan?.target_plan_id ? (
        <WorkoutEditSection
          key={selectedPlan.target_plan_id}
          planId={selectedPlan.target_plan_id}
          weekdayName={selectedPlan.weekday_name}
          userId={userId}
          showHeader={false}
          onChangeTemplate={handleChangeTemplateRequest}
          changingTemplate={isAssigningWeekday || changeTemplateConfirmOpen}
          onUseOnOtherDays={() => setUseOnOtherDaysOpen(true)}
          usingOnOtherDays={useOnOtherDaysOpen}
          onMarkAsRest={handleMarkAsRestRequest}
          markingAsRest={isMarkingAsRest || markAsRestConfirmOpen}
          onDurationChange={setWorkoutDurationSummary}
          copyTargetDays={assignableWeekdays}
        />
      ) : (
        <div className="text-center py-12 text-sm text-gray-500">
          Select a day above to edit exercises
        </div>
      )}

      <ChangeTemplateConfirmationModal
        isOpen={changeTemplateConfirmOpen}
        weekdayName={selectedPlan?.weekday_name}
        onConfirm={handleConfirmChangeTemplate}
        onCancel={() => setChangeTemplateConfirmOpen(false)}
      />

      <MarkAsRestConfirmationModal
        isOpen={markAsRestConfirmOpen}
        weekdayName={selectedPlan?.weekday_name}
        onConfirm={handleConfirmMarkAsRest}
        onCancel={() => !isMarkingAsRest && setMarkAsRestConfirmOpen(false)}
        isLoading={isMarkingAsRest}
      />

      <UseWorkoutOnOtherDaysModal
        isOpen={useOnOtherDaysOpen}
        sourceWeekdayName={selectedPlan?.weekday_name}
        days={assignableWeekdays}
        onConfirm={handleUseOnOtherDays}
        onClose={() => setUseOnOtherDaysOpen(false)}
      />

      <WorkoutTemplatePickerModal
        isOpen={Boolean(assigningWeekday)}
        weekdayName={assigningWeekday?.name}
        onSelect={handleTemplateSelect}
        onClose={() => !isAssigningWeekday && setAssigningWeekday(null)}
      />
    </div>
  );
}
